import { Paper, Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Layout, useMiscellaneousRoutes, useUserStore } from '@roc/feature-app-core';
import { routeByLoanSubType } from '@roc/feature-loans';
import { insertIf, isNil, LoanSubType } from '@roc/feature-utils';
import { StepObj } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { ReactNode, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useOneToolRoutes } from '../hooks/useOneToolRoutes';
import { QuoteTypeStore } from '../stores/quoteTypeStore';
import { QuoteStep, SelectEntityOption } from '../utils/constants';
import { QuoteTypeStoreContext } from '../utils/quoteTypeStoreContext';
import { getQuoteSteps } from '../utils/utils';
import { OneToolQuoteStepsContainer } from './oneToolQuoteStepsContainer';
import { RentalPortfolioQuoteStore } from '../stores/rentalPortfolioQuoteStore';
import { useStore } from '@roc/client-portal-shared/hooks';
import { PlatformCapitalProvider } from '../../components/common/PlatformCapitalProvider';

const useStyles = makeStyles(theme => ({
  // no inner padding: the steps line up with the page title
  paper: {
    padding: 0,
  },
}));

interface Props {
  store: QuoteTypeStore;
  title: string;
  children: Array<JSX.Element>;
  preflightStep: any;
  successStep: any;
}

interface Step extends StepObj {
  info?: string;
  size?: number;
}

const TERM_DISABLED_NAVIGATION_STEPS = [QuoteStep.LOAN_PRICER, QuoteStep.PORTFOLIO_INFORMATION];

export const OneToolQuoteStepper = observer(
  ({ store, title, children, preflightStep, successStep }: Props) => {
    const { globalStore} = useStore();
    const classes = useStyles();
    const { quoteId } = useParams<{ quoteId: string }>();
    const { push } = useHistory();
    const { oneToolRoutesConfig } = useOneToolRoutes();
    const { userStore } = useUserStore();
    const { allowLoanPricing } = userStore;
    const quoteSteps = getQuoteSteps(store.loanSubtype, store.allowPricerStep);
    const lastCompletedStepIndex = quoteSteps.indexOf(store.lastCompletedStep);
    const { entityStore } = store;
    const { selectEntityOption } = entityStore;

    const { miscellaneousRoutesConfig } = useMiscellaneousRoutes();
    const isTotalLoanAmountGreaterThan3M = store.loanEconomicsStore.isTotalLoanAmountGreaterThan3M;

    const isSinglePropertyRental = store.loanSubtype === LoanSubType.SINGLE_PROPERTY;
    const isRentalPortfolio = store.loanSubtype === LoanSubType.RENTAL_PORTFOLIO;

    const isTerm = isSinglePropertyRental || isRentalPortfolio;

    const disableNavigation = isTerm ? TERM_DISABLED_NAVIGATION_STEPS.includes(store.step) : isTotalLoanAmountGreaterThan3M;

    const isManualQuoteEnabled = store.loanEconomicsStore.enableManualQuote;
    const platformLoanSubmissionEnabled = globalStore.userFeatures.platformLoanSubmissionEnabled;

    useEffect(() => {
      store.reset();
      if (quoteId) {
        store.loadQuote(parseInt(quoteId));
      } else {
        store.createNewQuote();
      }
    }, []);

    useEffect(() => {
      if (!store.isLoanQuoteDetailsSuccess) {
        push(miscellaneousRoutesConfig.notFound.url);
      }
    }, [store.isLoanQuoteDetailsSuccess]);

    useEffect(() => {
      if (
        !quoteId &&
        store.quoteId &&
        !store.isEditingLoan &&
        store.step != QuoteStep.ENTITY_INFORMATION
      ) {
        const route =
          oneToolRoutesConfig[routeByLoanSubType[store.loanSubtype]];
        push(route(store.quoteId).url);
      }
    }, [quoteId, store.quoteId]);

    // Start every step at the top, however it was reached (Next/Back, the step bar, or an alert link)
    useEffect(() => {
      window.scrollTo({ top: 0 });
    }, [store.step]);

    if (isNil(store.step)) {
      return null;
    }

    const quoteData = store.getQuoteData();

    const borrowerInfo =
      lastCompletedStepIndex >=
        quoteSteps.indexOf(QuoteStep.BORROWER_INFORMATION)
        ? quoteData.borrowers
          .map(b => [b.firstName, b.lastName].join(' '))
          .join(', ')
        : '';

    const propertyInfo =
      lastCompletedStepIndex >=
        quoteSteps.indexOf(QuoteStep.PROPERTY_INFORMATION)
        ? quoteData.properties.map((p: any) => p.address).join(', ')
        : '';

    const leverageInfo =
      lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.LEVERAGE)
        ? store.loanEconomicsStore.calculateKeyRatios()
        : '';

    const loanEconomicsInfo =
      lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.LOAN_ECONOMICS)
        ? formatCurrency(store.loanEconomicsStore.outputs?.lenderTotalEarnings)
        : '';

    const correctPropertyInformationNextStep = () => {
      if (isSinglePropertyRental || isRentalPortfolio) {
        if (store.allowPricerStep) {
          return QuoteStep.LOAN_PRICER;
        }
        return QuoteStep.SUMMARY;
      }
      return QuoteStep.LEVERAGE;
    }

    const correctSummaryBackStep = () => {
      if (isSinglePropertyRental) {
        return store.allowPricerStep ? QuoteStep.LOAN_PRICER : QuoteStep.PROPERTY_INFORMATION;
      }
      if (isRentalPortfolio) {
        return store.allowPricerStep ? QuoteStep.LOAN_PRICER : QuoteStep.PORTFOLIO_INFORMATION;
      }

      return QuoteStep.LOAN_ECONOMICS;
    }

    const nextButtonText = store.isEditingLoan ? 'CONTINUE' : 'SAVE & CONTINUE';

    const steps: Step[] = [
      {
        title: 'Entity Information',
        icon: null,
        onBack: () => store.entityStore.setSelectEntityOption(null),
        onNext: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
        allowBack:
          !!selectEntityOption &&
          selectEntityOption !== SelectEntityOption.NO_ENTITY,
        allowNext: !!selectEntityOption,
        nextButtonText: nextButtonText,
        info: borrowerInfo,
        size: 271,
      },
      {
        title: 'Borrower Information',
        icon: null,
        onBack: () => store.goToStep(QuoteStep.ENTITY_INFORMATION),
        onNext: () => store.goToStep(isRentalPortfolio ? QuoteStep.PORTFOLIO_INFORMATION : QuoteStep.PROPERTY_INFORMATION),
        allowBack: true,
        allowNext: true,
        nextButtonText: nextButtonText,
        info: borrowerInfo,
        size: 271,
      },
      ...insertIf(!isRentalPortfolio, [{
        title: 'Property Information',
        icon: null,
        onBack: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
        onNext: () => store.goToStep(correctPropertyInformationNextStep()),
        allowBack: true,
        allowNext: true,
        nextButtonText: nextButtonText,
        info: propertyInfo,
        size: 400,
      }]),
      ...insertIf(isRentalPortfolio, [{
        title: 'Portfolio Information',
        icon: null,
        onBack: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
        onNext: () => {
          if (isRentalPortfolio) {
            const rentalPortfolioQuoteStore = store as RentalPortfolioQuoteStore;
            rentalPortfolioQuoteStore.prepopulateProperties();
          }
          store.goToStep(store.allowPricerStep ? QuoteStep.LOAN_PRICER : QuoteStep.SUMMARY);
        },
        allowBack: true,
        allowNext: true,
        nextButtonText: 'SAVE & CONTINUE',
        info: "",
        size: 400,
      }]),
      ...insertIf(!isSinglePropertyRental && !isRentalPortfolio, [{
        title: 'Leverage',
        icon: null,
        onBack: () => store.goToStep(QuoteStep.PROPERTY_INFORMATION),
        onNext: () => store.goToStep(QuoteStep.LOAN_ECONOMICS),
        allowBack: true,
        allowNext: true,
        disableNextButton: ((!store.loanEconomicsStore.calculatedFields
          .totalLoanAmount && !isManualQuoteEnabled) || isTotalLoanAmountGreaterThan3M),
        nextButtonText: nextButtonText,
        info: leverageInfo,
        size: 130,
      },
      {
        title: 'Loan Economics',
        icon: null,
        onBack: () => store.goToStep(QuoteStep.LEVERAGE),
        onNext: () => store.goToStep(QuoteStep.SUMMARY),
        allowBack: true,
        allowNext: true,
        nextButtonText: nextButtonText,
        info: loanEconomicsInfo,
        size: 130,
      },]),
      ...insertIf(((isSinglePropertyRental || isRentalPortfolio) && store.allowPricerStep), [
        {
          title: 'Loan Pricer',
          icon: null,
          onBack: () => store.goToStep(isRentalPortfolio ? QuoteStep.PORTFOLIO_INFORMATION : QuoteStep.PROPERTY_INFORMATION),
          onNext: () => store.goToStep(QuoteStep.SUMMARY),
          allowBack: true,
          allowNext: false,
          nextButtonText: nextButtonText,
          info: "",
          size: 130,
        },]),
      {
        title: 'Summary',
        icon: null,

        onBack: () => store.goToStep(correctSummaryBackStep()),
        onNext: () => {
          if (store.validateCurrentStep()) {
            store.summaryStore.loanCommentsFormStore.openDialog();
          }
        },
        allowBack: true,
        allowNext: true,
        nextButtonText: (store.loanEconomicsStore.enableManualQuote || !allowLoanPricing)
          ? nextButtonText
          : 'ACCEPT QUOTE',
        size: 115,
      },
    ];

    if (store.step === QuoteStep.PREFLIGHT) {
      return (
        <QuoteTypeStoreContext.Provider value={store}>
          {preflightStep}
        </QuoteTypeStoreContext.Provider>
      );
    }
    if (store.step === QuoteStep.SUCCESS) {
      return (
        <QuoteTypeStoreContext.Provider value={store}>
          {successStep}
        </QuoteTypeStoreContext.Provider>
      );
    }

    return (
      <QuoteTypeStoreContext.Provider value={store}>
        <Layout maxWidth={'lg'}>
          <Grid container direction="row" spacing={2} alignItems='center' style={{ marginTop: 16, marginBottom: 24 }} >
            <Grid item xs={12} sm={8}>
              <Typography align="left" variant="h4" style={{ color: 'black' }}>
                {title}
              </Typography>
            </Grid>
            {!!platformLoanSubmissionEnabled && (
              <Grid item xs={12} sm={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PlatformCapitalProvider />
              </Grid>
            )}
          </Grid>
          <Paper className={classes.paper} elevation={0}>
            <OneToolQuoteStepsContainer
              activeStep={quoteSteps.indexOf(store.step)}
              disableNavigation={disableNavigation}
              steps={steps}
              handleStep={index => store.goToStep(quoteSteps[index])}
            >
              {children}
            </OneToolQuoteStepsContainer>
          </Paper>
        </Layout>
      </QuoteTypeStoreContext.Provider>
    );
  }
);
